/**
 * usePricing — fetches live package pricing from the server.
 *
 * Returns:
 *   packages   — array of package objects (same shape used by BookLesson + Pricing)
 *   pricing    — raw pricing object { trial_price, trial_upfront, … }
 *   loading    — true while fetching
 *
 * Caches the result in module-level state so multiple components on the
 * same page share one request. The cache is busted when the module reloads
 * (i.e. on every fresh page load / React refresh), which is fine.
 */

import { useState, useEffect } from 'react';
import api from '../utils/api';

// --- Sensible defaults (used while loading or on fetch error) ---
const DEFAULTS = {
  trial_hours: 2,
  trial_price: 80,
  trial_upfront: 40,
  bronze_hours: 10,
  bronze_price: 360,
  bronze_upfront: 100,
  silver_hours: 20,
  silver_price: 710,
  silver_upfront: 150,
  gold_hours: 30,
  gold_price: 1050,
  gold_upfront: 200,
  hourly_rate: 37,
};

// Module-level cache so sibling components share one fetch
let _cache = null;
let _promise = null;

function fetchPricing() {
  if (_cache) return Promise.resolve(_cache);
  if (_promise) return _promise;
  _promise = api.get('/public/pricing')
    .then(res => {
      const p = res?.data?.pricing || res?.pricing || DEFAULTS;
      _cache = { ...DEFAULTS, ...p };
      return _cache;
    })
    .catch(() => {
      _cache = { ...DEFAULTS };
      return _cache;
    });
  return _promise;
}

/** Convert a raw pricing object into the packages array. */
export function buildPackages(p) {
  const hourly = p.hourly_rate || DEFAULTS.hourly_rate;
  const tHours = p.trial_hours ?? DEFAULTS.trial_hours;
  const bHours = p.bronze_hours ?? DEFAULTS.bronze_hours;
  const sHours = p.silver_hours ?? DEFAULTS.silver_hours;
  const gHours = p.gold_hours ?? DEFAULTS.gold_hours;
  return [
    {
      id: 'trial',
      label: 'Trial Lesson',
      name: 'Trial Lesson',
      hours: tHours,
      price: p.trial_price ?? DEFAULTS.trial_price,
      packagePrice: p.trial_price ?? DEFAULTS.trial_price,
      upfront: p.trial_upfront ?? DEFAULTS.trial_upfront,
      remaining: (p.trial_price ?? DEFAULTS.trial_price) - (p.trial_upfront ?? DEFAULTS.trial_upfront),
      standardPrice: Math.round(tHours * hourly),
      saving: Math.max(0, Math.round(tHours * hourly) - (p.trial_price ?? DEFAULTS.trial_price)),
      savings: Math.max(0, Math.round(tHours * hourly) - (p.trial_price ?? DEFAULTS.trial_price)),
      discount: Math.round(Math.max(0, Math.round(tHours * hourly) - (p.trial_price ?? DEFAULTS.trial_price)) / Math.round(tHours * hourly) * 100),
      tag: null,
      colour: 'default',
      tagColour: '',
      desc: 'Drive with a DVSA-approved instructor. No commitment required',
      description: `Try before you commit. Meet your instructor, experience driving, and continue at £${hourly}/hr — paid directly to them.`,
    },
    {
      id: 'bronze',
      label: 'Bronze',
      name: 'Bronze',
      hours: bHours,
      price: p.bronze_price ?? DEFAULTS.bronze_price,
      packagePrice: p.bronze_price ?? DEFAULTS.bronze_price,
      upfront: p.bronze_upfront ?? DEFAULTS.bronze_upfront,
      remaining: (p.bronze_price ?? DEFAULTS.bronze_price) - (p.bronze_upfront ?? DEFAULTS.bronze_upfront),
      standardPrice: Math.round(bHours * hourly),
      saving: Math.max(0, Math.round(bHours * hourly) - (p.bronze_price ?? DEFAULTS.bronze_price)),
      savings: Math.max(0, Math.round(bHours * hourly) - (p.bronze_price ?? DEFAULTS.bronze_price)),
      discount: Math.round(Math.max(0, Math.round(bHours * hourly) - (p.bronze_price ?? DEFAULTS.bronze_price)) / Math.round(bHours * hourly) * 100),
      tag: null,
      colour: 'bronze',
      tagColour: '',
      desc: 'A solid foundation block — great for beginners building core skills and road confidence.',
      description: `${bHours} hours to build real confidence. Pay £${p.bronze_upfront ?? DEFAULTS.bronze_upfront} now — remaining £${(p.bronze_price ?? DEFAULTS.bronze_price) - (p.bronze_upfront ?? DEFAULTS.bronze_upfront)} paid directly to your instructor.`,
    },
    {
      id: 'silver',
      label: 'Silver',
      name: 'Silver',
      hours: sHours,
      price: p.silver_price ?? DEFAULTS.silver_price,
      packagePrice: p.silver_price ?? DEFAULTS.silver_price,
      upfront: p.silver_upfront ?? DEFAULTS.silver_upfront,
      remaining: (p.silver_price ?? DEFAULTS.silver_price) - (p.silver_upfront ?? DEFAULTS.silver_upfront),
      standardPrice: Math.round(sHours * hourly),
      saving: Math.max(0, Math.round(sHours * hourly) - (p.silver_price ?? DEFAULTS.silver_price)),
      savings: Math.max(0, Math.round(sHours * hourly) - (p.silver_price ?? DEFAULTS.silver_price)),
      discount: Math.round(Math.max(0, Math.round(sHours * hourly) - (p.silver_price ?? DEFAULTS.silver_price)) / Math.round(sHours * hourly) * 100),
      tag: 'Most Popular',
      colour: 'silver',
      tagColour: '#CF142B',
      desc: 'The most popular choice. Cover everything you need to progress confidently towards your test.',
      description: `${sHours} hours — ideal for learners aiming to test within 2–3 months. Pay £${p.silver_upfront ?? DEFAULTS.silver_upfront} now, £${(p.silver_price ?? DEFAULTS.silver_price) - (p.silver_upfront ?? DEFAULTS.silver_upfront)} to instructor.`,
    },
    {
      id: 'gold',
      label: 'Gold',
      name: 'Gold',
      hours: gHours,
      price: p.gold_price ?? DEFAULTS.gold_price,
      packagePrice: p.gold_price ?? DEFAULTS.gold_price,
      upfront: p.gold_upfront ?? DEFAULTS.gold_upfront,
      remaining: (p.gold_price ?? DEFAULTS.gold_price) - (p.gold_upfront ?? DEFAULTS.gold_upfront),
      standardPrice: Math.round(gHours * hourly),
      saving: Math.max(0, Math.round(gHours * hourly) - (p.gold_price ?? DEFAULTS.gold_price)),
      savings: Math.max(0, Math.round(gHours * hourly) - (p.gold_price ?? DEFAULTS.gold_price)),
      discount: Math.round(Math.max(0, Math.round(gHours * hourly) - (p.gold_price ?? DEFAULTS.gold_price)) / Math.round(gHours * hourly) * 100),
      tag: 'Best Value',
      colour: 'gold',
      tagColour: '#d4a017',
      desc: 'Maximum value for complete beginners — all the hours to go from zero to test-ready.',
      description: `${gHours} hours — intensive course, fastest route to your test. Pay £${p.gold_upfront ?? DEFAULTS.gold_upfront} now, £${(p.gold_price ?? DEFAULTS.gold_price) - (p.gold_upfront ?? DEFAULTS.gold_upfront)} to instructor.`,
    },
  ];
}

export default function usePricing() {
  const [pricing, setPricing] = useState(_cache || DEFAULTS);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) { setPricing(_cache); setLoading(false); return; }
    fetchPricing().then(p => { setPricing(p); setLoading(false); });
  }, []);

  return {
    pricing,
    packages: buildPackages(pricing),
    loading,
  };
}
